<template>
    <MainCard>
        <div class="flex flex-col items-center animate-fade-in p-5 ">
            <canvas class="" ref="wheelCanvas" :width="wheelSize" :height="wheelSize"></canvas>

        </div>
    </MainCard>
</template>

<script setup>
import { ref, onMounted, computed, watch, defineEmits } from "vue";
import { useParticipationStore } from "@/stores/useParticipationStore";
import { useRouter } from "vue-router";
import MainCard from "@/components/mainCard.vue";

const router = useRouter();
const participationStore = useParticipationStore();
const gifts = computed(() => participationStore.gifts);

const wheelCanvas = ref(null);
const ctx = ref(null);

const wheelSize = 350;
const center = wheelSize / 2;
const radius = wheelSize / 2 - 10;

const rotation = ref(0);
const isSpinning = ref(false);
const targetGiftId = ref(null);

const spinDuration = 4000;
const laps = 5;

const sliceAngle = computed(() => (2 * Math.PI) / gifts.value.length);
const giftIndexMap = computed(() => {
    const map = {};
    gifts.value.forEach((gift, i) => (map[gift.id] = i));
    return map;
});

const BRAND_COLORS = {
    cocacola: { primary: "#F40009", secondary: "#CC0007", accent: "#FFFFFF", light: "#C8C8C8", dark: "#000000" },
    fanta: { primary: "#FF7A00", secondary: "#E56700", accent: "#FBD300", light: "#FFFFFF", dark: "#000000" },
    sprite: { primary: "#009639", secondary: "#007A2F", accent: "#FDEF00", light: "#A7D300", dark: "#000000" },
    schweppes: { primary: "#FCE300", secondary: "#E0C700", accent: "#C8C8C8", light: "#FFFFFF", dark: "#000000" }
};

const brand = computed(() => participationStore.participation?.brand?.toLowerCase() || "cocacola");
const brandColors = computed(() => BRAND_COLORS[brand.value]);

const drawWheel = () => {
    if (!ctx.value) return;
    const c = ctx.value;
    c.clearRect(0, 0, wheelSize, wheelSize);

    gifts.value.forEach((gift, i) => {
        const startAngle = i * sliceAngle.value + rotation.value;
        const endAngle = startAngle + sliceAngle.value;

        const grad = c.createRadialGradient(center, center, 0, center, center, radius);

        const colorStart = i % 2 === 0 ? brandColors.value.primary : brandColors.value.secondary;
        const colorEnd = i % 2 === 0 ? brandColors.value.secondary : brandColors.value.primary;

        grad.addColorStop(0, colorStart);
        grad.addColorStop(1, colorEnd);

        c.beginPath();
        c.moveTo(center, center);
        c.arc(center, center, radius, startAngle, endAngle);
        c.closePath();
        c.fillStyle = grad;
        c.fill();

        c.strokeStyle = "#e2e2e2ff";
        c.lineWidth = 5;
        c.stroke();

        const textAngle = startAngle + sliceAngle.value / 2;
        c.save();
        c.translate(center, center);
        c.rotate(textAngle);
        const textRadius = radius / 1.6;
        c.textAlign = "center";
        c.textBaseline = "middle";
        c.fillStyle = brandColors.value.accent;
        c.font = "bold 16px Arial"; 
        c.fillText(gift.name, textRadius, 0);
        c.restore();
    });

    const centerRadius = 40;
    c.beginPath();
    c.arc(center, center, centerRadius, 0, 2 * Math.PI);
    c.fillStyle = brandColors.value.accent;
    c.fill();
    c.strokeStyle = "#e2e2e2ff";
    c.lineWidth = 6;
    c.stroke();

    c.fillStyle = brandColors.value.primary;
    c.font = "bold 13px Arial";
    c.textAlign = "center";
    c.textBaseline = "middle";
    c.fillText("SPIN", center, center);

    c.beginPath();
    c.arc(center, center, radius, 0, 2 * Math.PI);
    c.lineWidth = 9;
    c.strokeStyle = "#e2e2e2ff";
    c.stroke();

    const pointerHalfWidth = 8;
    const pointerSize = 20;
    c.beginPath();
    c.moveTo(center + radius - pointerSize, center);
    c.lineTo(center + radius, center - pointerHalfWidth);
    c.lineTo(center + radius, center + pointerHalfWidth);
    c.closePath();
    c.fillStyle = brandColors.value.dark;
    c.fill();
};

const animateSpin = (targetIndex) => {
    if (isSpinning.value) return;
    isSpinning.value = true;

    const startRotation = 0;
    rotation.value = 0;
    drawWheel();

    const fullRotation = 2 * Math.PI * laps;
    const stopAngle = (2 * Math.PI - targetIndex * sliceAngle.value - sliceAngle.value / 2) % (2 * Math.PI);
    const finalRotation = fullRotation + stopAngle;

    const startTime = performance.now();
    const animate = (time) => {
        const elapsed = time - startTime;
        const t = Math.min(elapsed / spinDuration, 1);
        const easeOut = 1 - Math.pow(1 - t, 3);
        rotation.value = startRotation + easeOut * finalRotation;
        drawWheel();
        if (t < 1) requestAnimationFrame(animate);
        else {
            isSpinning.value = false;
            console.log("Winner:", gifts.value[targetIndex]);
            participationStore.selectedGift = gifts.value[targetIndex];
            setTimeout(() => {
                router.push({ name: "celebration", params: { code: participationStore.link } });
            }, 1000);
        }
    };
    requestAnimationFrame(animate);
};

const spinWheel = async () => {
    if (!gifts.value.length) return;

    try {
        const response = await participationStore.spinTheWheel();
        if (!response.success) {
            console.error("Wheel spin failed:", response.message);
            return;
        }

        targetGiftId.value = participationStore.participation.gift_id;
        const winnerId = targetGiftId.value;
        const targetIndex = giftIndexMap.value[winnerId];
        if (targetIndex != null) animateSpin(targetIndex);
    } catch (error) {
        console.error("Spin request failed:", error);
    }
};

const emit = defineEmits('toggleBottleSuccess');

onMounted(() => {
    emit('toggleBottleSuccess');

    const canvas = wheelCanvas.value;
    ctx.value = canvas.getContext("2d");
    drawWheel();

    wheelCanvas.value.addEventListener("click", (event) => {
        const rect = wheelCanvas.value.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        const dx = x - center;
        const dy = y - center;
        if (Math.sqrt(dx * dx + dy * dy) <= center) spinWheel();
    });
});

watch(brand, () => {
    drawWheel();
});
</script>


<style scoped>
canvas {
    display: block;
}

@keyframes fade-in {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animate-fade-in {
    animation: fade-in 0.8s ease-out forwards;
}
</style>
